import { JSX, Suspense, useMemo } from "react";

import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import { Alert, AlertTitle, Box, Grid, LinearProgress, Typography, useTheme } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { apiGetLocationDashboard } from "src/api/locationCharting.api";
import { scrollGridItem } from "src/components/InfoBar/infoBar.helper";
import ScrollGridInfoBar from "src/components/InfoBar/ScrollGridInforBar";
import { GetLocationDashboardQueryKey } from "src/components/LocationBreadcrumbs/helpers/breadcrumbs.helper";
import IntervalType from "src/constants/intervalType.enum";
import MeasureType from "src/constants/measureType.enum";
import { getIntervalPollingSettings } from "src/lib/pollingSettings.helper";
import useAppPinnedStore from "src/store/pinned.store";
import { type MeasuresSummarizedDto } from "src/types/dashboardDatasetDto";
import type LocationType from "src/types/LocationType";

import PinnedItemComponent from "./PinnedItemComponent";

const styles = {
  pinnedTextBox: {
    mx: { xs: 0, sm: 0 },
  },
  fullWidth: { xs: 12 },
  favourite: { md: 2 },
};

function PinnedComponent({ displayedType }: { displayedType: LocationType }): JSX.Element {
  const { t } = useTranslation();
  const theme = useTheme();
  const { appPinned } = useAppPinnedStore();

  const fileredAppPinnedLocationIds = useMemo(
    () => appPinned.filter((loc) => loc.type === displayedType).map((item) => item.id),
    [appPinned, displayedType]
  );
  const isPinned = useMemo(() => fileredAppPinnedLocationIds.length > 0, [fileredAppPinnedLocationIds]);

  const pinnedQueryKey = GetLocationDashboardQueryKey(fileredAppPinnedLocationIds, IntervalType.Day, null, MeasureType.Count, false, 0, null, null, isPinned);
  const { data: measuresData } = useQuery<MeasuresSummarizedDto, Error>({
    queryKey: pinnedQueryKey,
    queryFn: async () =>
      apiGetLocationDashboard(fileredAppPinnedLocationIds, IntervalType.Day, null, MeasureType.Count, false, 0, null, null, isPinned),
    ...getIntervalPollingSettings(IntervalType.Day, null),
    enabled: isPinned,
  });

  const pinnedMeasuresData = useMemo(() => measuresData?.data?.slice(0, 5) ?? [], [measuresData?.data]);
  const filteredPinnedMeasuresData = useMemo(
    () => pinnedMeasuresData.filter((favorite) => favorite.type === displayedType),
    [displayedType, pinnedMeasuresData]
  );

  return (
    <Grid container spacing={2}>
      <Grid size={styles.fullWidth}>
        <Box sx={styles.pinnedTextBox}>
          <Typography
            color={theme.palette.text.secondary}
            fontSize={theme.fontSizes.sm}
            fontWeight={theme.typography.fontWeightRegular}
            textTransform='uppercase'
          >
            {t("Pinned")}
          </Typography>
        </Box>
      </Grid>
      <Grid size={styles.fullWidth}>
        <Suspense fallback={<LinearProgress />}>
          {measuresData ? (
            <ScrollGridInfoBar columns={10}>
              {filteredPinnedMeasuresData.map((favorite) => (
                <Grid size={styles.favourite} key={favorite.locationId} sx={scrollGridItem(180, 240)}>
                  <PinnedItemComponent data={favorite} />
                </Grid>
              ))}
            </ScrollGridInfoBar>
          ) : isPinned ? (
            <LinearProgress />
          ) : null}
        </Suspense>
      </Grid>
      {!isPinned && (
        <Grid size={styles.fullWidth}>
          <Alert severity='success' icon={<AutoFixHighIcon />}>
            <AlertTitle>{t(`Quickly access key insights by pinning dashboards.`)}</AlertTitle>
            {t(`Pinned dashboards will appear at the top of your sidebar, ensuring seamless navigation and efficiency.`)}
          </Alert>
        </Grid>
      )}
    </Grid>
  );
}

export default PinnedComponent;
